<template>
  <div class="edit-query bool-card">
    <header class="card-header">
      <div class="flex items-center justify-between">
        <div class="flex items-center">
          <a class="go-back-icon" @click.prevent="backToListPage"><i class="fal fa-chevron-left"/></a>
          <h3>{{title}}
            <span v-if="model.id" class="query-uid" @click="simpleCopy(model.id)">#{{model.id}}</span>
          </h3>
        </div>
        <div v-if="!isCloned" class="actions">
          <div v-if="model.id" class="clone" @click="cloneToggle"><i class="far fa-copy"/><span>Clone</span></div>

          <dropdownButton
              v-if="model.id && !isGroup"
              :options="exportOptions"
              :icon="queryExporting && queryExporting === model.id ? 'fas fa-spinner rotate' : 'far fa-download'"
              :label="$t('booleanSearch.export')"
              :customCss="`search-export-dropdown ${queryExporting ? 'disabled' : ''}`"
              @exportCsv="exportSearches({ key: resourceName, item: model.id, formatType: 'csv' })"
              @exportXls="exportSearches({ key: resourceName, item: model.id, formatType: 'xlsx' })"
          >
          </dropdownButton>

          <div v-if="model.id" class="reset" @click="reset"><i class="far fa-sync-alt" :class="{'rotate-180' : resetting}"/><span>Reset</span></div>
          <div class="clone" @click="triggerFormValidation"><i class="far fa-save"/><span>Save</span></div>
        </div>
      </div>
    </header>

    <section class="form-section" :class="{'display-none': validationMessages.length === 0 && errorMessage === ''}">
      <ul ref="error_msgs" class="error-msgs">
        <li v-if="errorMessage !== ''">{{errorMessage}}</li>
        <li v-for="(message, i) in validationMessages" :key="i" v-html="message"></li>
      </ul>
    </section>

    <div v-if="isCloned" class="clone-section">
      <clone-overlay
          :item="model"
          :resourceName="resourceName"
          @close="resetCloneToggle"
          @cloneResource="execCloneResource"
      />
    </div>

    <div v-else-if="showCreateTag" class="clone-section">
      <create-tag-overlay
        :search="model"
        :searchQueryKeys="searchQueryKeys"
        @close="createTagToggle(false)"
        @updateSearchTags="(value) => this.updateModel({ tags: value }, { name: 'tags' })"
      />
    </div>

    <main v-else>
      <!-- Details-->
      <collapsibleFormSection :translatable-title="'booleanSearch.details.title'">
        <form-renderer
            id="detail-form"
            form-wrapper-class="detailsFormWrapper"
            :model="model"
            :fields="detailFields"
            :fieldsAdditions="detailsFieldsAdditions"
            :customValidators="customValidators"
            :showSubmit="false"
            :validateFormTrigger="validateFormTrigger"
            :validateFieldTrigger="validateFieldTrigger"
            :showMessageNotifications="false"
            @updateModel="(value, field) => this.updateModel(value, field)"
            @onFieldBlur="triggerFieldValidation"
        />
      </collapsibleFormSection>

      <!-- Filters-->
      <collapsibleFormSection v-if="!hideFilters" :translatable-title="'booleanSearch.filters.title'">
        <form-renderer
            id="filter-form"
            form-wrapper-class="filterFormWrapper"
            :model="model"
            :fields="filterFields"
            :fieldsAdditions="filtersFieldsAdditions"
            :customValidators="customValidators"
            :showSubmit="false"
            :validateFormTrigger="validateFormTrigger"
            :validateFieldTrigger="validateFieldTrigger"
            :showMessageNotifications="false"
            @updateModel="(value, field) => this.updateModel(value, field)"
            @onFieldBlur="triggerFieldValidation"
        />
      </collapsibleFormSection>
      <collapsibleFormSection v-if="isTagSearch && model.searches && model.searches.length > 0" 
        :translatable-title="'booleanSearch.queries.linked_title'"
        :infoTooltip="$t('booleanSearch.queries.link_search_info')">
        <div class="search-tags-wrapper">
          <Datatable 
            ref="tagsDataTable"
            :data="searchesTable"
            :noResults="$t('booleanSearch.tags.no_results')"
          />
        </div>
      </collapsibleFormSection>

      <!-- Queries -->
      <collapsibleFormSection v-else :translatable-title="'booleanSearch.queries.title'">
        <div class="info-section">
          <div v-if="model.search_type === 'tveyes_podcast'">
            <div :class="{validation: true}">{{$t('booleanSearch.podcast_instructions')}}<span class="font-bold text-italic">{{$t('booleanSearch.podcast_text')}}</span></div>
          </div>
          <div v-if="showIgnoredCharacters" :class="{validation: true}">{{$t('booleanSearch.ignored_chars')}}<span class="font-bold text-italic">{{$t('booleanSearch.ignored_chars_list')}}</span></div>
        </div>
        <form-renderer            
            id="query-form"
            form-wrapper-class="queryFormWrapper"
            :model="model"
            :fields="searchQueryKeys"
            :fieldsAdditions="queryFieldsAdditions"
            :showSubmit="false"
            :validateFormTrigger="validateFormTrigger"
            :validateFieldTrigger="validateFieldTrigger"
            :showMessageNotifications="false"
            :syncModel="true"
            @updateModel="(value, field) => this.updateModel(value, field)"
            @onFieldBlur="triggerFieldValidation"
        />
      </collapsibleFormSection>

      <!-- Tags -->
      <collapsibleFormSection v-if="isAllowedAddTag" 
        :translatable-title="'booleanSearch.tags.title'"
        :infoTooltip="$t('booleanSearch.tags.link_tag_info')">
        <div class="search-tags-wrapper">
          <Datatable 
            ref="tagsDataTable"
            :data="tagsTable"
            :noResults="$t('booleanSearch.tags.no_results')"
          />
        </div>
      </collapsibleFormSection>

      <div class="text-right">
        <button class="button button-save mr-2" @click="triggerFormValidation">Save</button>
        <button v-if="model.id" class="button button-delete" @click="() => { showDeleteConfirmation = true; }">Delete</button>
      </div>
    </main>

    <confirmation-modal v-if="showDeleteConfirmation"
                        :headerText="$t('messages.confirm_delete.title')"
                        :message="$t('messages.confirm_delete.message', [searchName])"
                        :showDeleteButton="true"
                        @confirmAction="deleteItem"
                        @cancelAction="() => { showDeleteConfirmation = false; }" />

    <group-action-attn-modal v-if="groupBlocked.groups.length > 0 || groupBlocked.searches.length > 0"
                       @onRetry="retryGroupAction"
    />
  </div>
</template>

<script>
// Mixins
import cloning from "../../../mixins/boolean-search/cloning";
import remove from '../../../mixins/boolean-search/delete.js'
import simpleCopy from "../../../mixins/boolean-search/simpleCopy";
import exportSearches from '../../../mixins/boolean-search/export';

// Components
import cloneOverlay from './queryListItemComponents/CloneOverlay.vue'
import createTagOverlay from './queryListItemComponents/CreateTagOverlay';
import collapsibleFormSection from "../../transitions/CollapsibleFormSection";
import formRenderer from '../../form-renderer.vue'
import confirmationModal from '../../confirmation-modal';
import groupActionAttnModal from '../modals/GroupActionAttnModal';
import dropdownButton from '../../dropdown-button';
import Datatable from '@/components/datatable';

// Helpers
import FormHelper from '@/helpers/form';

//Config
import { searchTypes, multiSyntaxSearch } from '../../../config/bool-const';
import { INPUT_FIELDS } from '@/config/const';

//Plugins
import { mapState } from 'vuex';
import find from 'lodash/find';
import get from 'lodash/get';

export default {
  name: "SearchForm",
  props: {
    resourceName: {
      type: String,
      required: true
    },
    model: {
      type: Object,
      required: true
    },
    dbModel: {
      type: Object,
      required: true
    },
    detailFields: {
      type: Array,
      default: () => []
    },
    filterFields: {
      type: Array,
      default: () => []
    },
    detailsFieldsAdditions: {
      type: Object,
      default: () => {}
    },
    filtersFieldsAdditions: {
      type: Object,
      default: () => {}
    },
    queryFieldsAdditions: {
      type: Object,
      default: () => {}
    },
    customValidators: {
      type: Object,
      default: () => {}
    },
    filters: {
      type: Boolean,
      default: true
    },
    hideFilters: Boolean,
    title: {
      type: String,
      required: true
    },
    errorMessage: {
      type: String,
      default: ''
    },
    updateModel: Function
  },

  mixins: [ cloning, remove, simpleCopy, exportSearches ],

  data() {
    return {
      resetting: false,
      showDeleteConfirmation: false,
      validationMessages: [],
      validateFormTrigger: false,
      validateFieldTrigger: false,
      isQueryInvalid: false,
      searchName: '',
      showCreateTag: false
    }
  },

  created () {
    this.searchName = this.model.name;
    if(this.$props.errorMessage) {
      document.documentElement.scrollTop = document.body.scrollTop = 0
    }
  },

  computed: {
    ...mapState({
      client_id: state => state.booleanSearch.client_id,
      project_id: state => state.booleanSearch.project_id,
      groupBlocked: state => state.booleanSearch.group_blocked,
      queryExporting: state => state.booleanSearch.searches.queryExporting,
      tagSearch: state => state.booleanSearch.tagSearch
    }),

    searchQueryKeys () {
      let queries = [];
      if(this.model && this.model.is_multi) {
        queries = [...multiSyntaxSearch.queries];
        // Add media_track_query for multi-syntax searches only if 'asian' is among the selected providers
        if(Array.isArray(this.model.search_type) && this.model.search_type?.includes('asian')) {
          queries.push('media_track_query');
        }
      }
      else if(['group_library', 'group'].indexOf(this.resourceName) !== -1) {
        queries = ['query'];
      } 
      else if(['tag_library', 'tagsearch'].indexOf(this.resourceName.toLowerCase()) !== -1) {
        queries = ['regex_query', 'standard_query', 'user_query'];
      } 
      else if(this.model?.search_type) {
        let searchTypeOptions = find(searchTypes, type => type.value === this.model.search_type);
        queries = searchTypeOptions.queries;
      }

      return queries;
    },

    isGroup () {
      return ['group_library', 'group'].indexOf(this.resourceName) !== -1;
    },

    isTagSearch(){
      return ['tag_library', 'tagsearch'].indexOf(this.resourceName.toLowerCase()) !== -1;
    },

    exportOptions() {
      return [
        { label: 'Export CSV', action: 'exportCsv' },
        { label: 'Export Excel', action: 'exportXls' },
      ]
    },

    showIgnoredCharacters () {
      return !this.model.is_multi && ['x_netfeedr', 'x_netfeedr_channel', 'twitter_search_v3', 'socials'].indexOf(this.model.search_type) === -1;
    },
    
    isAllowedAddTag() {
      const currentSearchType = searchTypes.find(type => type.value === this.model.search_type);
      const searchTypeQueryNames = this.searchQueryKeys.map(q => INPUT_FIELDS[q].name);
      
      return (
        this.model.id && 
        !this.isTagSearch && 
        !this.isGroup && 
        ((this.model.is_multi || currentSearchType.allowTagLink) && searchTypeQueryNames.some(q => (this.dbModel.queries[q] || "").length))
      );
    },

    searchesTable() {
      return {
        body: this.model?.searches,
        columns: [
          {
            key: 'name',
            label: this.$i18n.t('admin.headers.name'),
            orderable: false,
            render: (row) => { return (<a class="link-pointer" ref={`row${row.id}`} href={this.getSearchUrl(row.id)}>{row.name}</a>); }
          }
        ]
      }
    },

    tagsTable() {
      return {
        body: this.model?.tags,
        columns: [
          {
            key: 'name',
            label: this.$i18n.t('admin.headers.name'),
            orderable: false,
            render: (row) => { return (<a class="link-pointer" ref={`row${row.id}`} href={this.getTagUrl(row.id)}>{row.name}</a>); }
          }
        ],
        addRow: {
          enabled: true,
          label: this.$i18n.t('booleanSearch.tags.manage_tags'),
          onClick: () => this.createTagToggle()
        },
      }
    }
  },

  methods: {
    getSearchUrl(searchId) {
      return `/clients/${this.client_id}/projects/${this.project_id}/searches/${searchId}`;
    },
    backToListPage() {
      this.isTagSearch ? 
        this.$router.push(`/clients/${this.client_id}/projects/${this.project_id}/tags`) :
        this.$router.push(`/clients/${this.client_id}/projects/${this.project_id}/searches${this.isGroup ? '/group_library' : ''}`);
    },
    reset() {
      this.$emit('reset')
      this.resetting = true
      setTimeout(() => this.resetting = false, 250)
    },

    deleteItem () {
      this.remove({ key: this.resourceName, item: this.model });
      this.showDeleteConfirmation = false;
    },

    saveModel () {
      if (this.validationMessages.length === 0) {
        this.$emit('submit')
      }
      else {
        window.scrollTo({top: 0, behavior: 'smooth'})
      }
    },

    triggerFormValidation() {
      // There is a bug if we directly set validateFormTrigger to true when
      // loading by direct url and click save directly
      // so we set it to new Boolean(true); and then to true 
      // otherwise the model does not contains yet all fields like standard_query
      // and will say query is mandatory even if the standard query has value.
      this.validateFormTrigger = new Boolean(true);

      this.$nextTick(() => {
        this.validateFormTrigger = true;
        this.$nextTick(() => {
          // find if any forms have errors
          let errors = [];
          this.$children.map((child) => {
            if(get(child, '$children[0].$children[0].$children[0].validationMessages') &&
              get(child, '$children[0].$children[0].$children[0].validationMessages').length) {
              errors = [...errors, ...get(child, '$children[0].$children[0].$children[0].validationMessages')];
            }
          });

          this.validationMessages = errors;

          this.saveModel();
          this.validateFormTrigger = false;
        })
      });
    },

    triggerFieldValidation({field, value, formId}) {
      this.resetSaveError();

      if(this.validateFormTrigger) return;

      this.validateFieldTrigger = { field: field, value: value, formId: formId };

      this.$nextTick(() => {
        this.validateFieldTrigger = false;
        // find if any forms have errors
        let errors = [];
        this.$children.map((child) => {
          if(get(child, '$children[0].$children[0].$children[0].validationMessages') &&
            get(child, '$children[0].$children[0].$children[0].validationMessages').length) {
            errors = [...errors, ...get(child, '$children[0].$children[0].$children[0].validationMessages')];
          }
        });

        this.validationMessages = errors;
      });
    },

    retryGroupAction() {
      if(!this.groupBlocked) return;

      switch (this.groupBlocked.action) {
        case 'delete'  :
          this.remove({ key: this.resourceName, item: this.model });
          break;
        case 'edit':
          this.$emit('submit')
          break;
        default:
          return;
      }
    },

    resetSaveError() {
      if(this.errorMessage !== '') {
        this.$store.commit('booleanSearch/SET_SAVE_ERROR', { key: this.resourceName, message: '' });
      }
    },

    createTagToggle (isOpen) {
      if(isOpen === undefined) isOpen = !this.showCreateTag;
      this.showCreateTag = isOpen;

      if(isOpen) {
        window.scrollTo({top: 0, behavior: 'smooth'});
      }
      else {
        FormHelper.delayFunction(() => {
          let dataTableEl = this.$refs.tagsDataTable.$el;
          if(dataTableEl) dataTableEl?.scrollIntoView({ behavior: 'smooth' }) ;
        }, 5, this);
      }
    },

    getTagUrl(tagId) {
      return `/clients/${this.client_id}/projects/${this.project_id}/tags/${tagId}`;
    }
  },

  components: {
    collapsibleFormSection,
    cloneOverlay,
    createTagOverlay,
    formRenderer,
    confirmationModal,
    groupActionAttnModal,
    dropdownButton,
    Datatable
  }
}
</script>